import React, { useMemo, useReducer } from "react";
import { useSearchParams } from "react-router-dom";

import { emailRegex, splitEmailList } from "../../lib/util";
import { Plan, searchParamNames, selfServicePlans } from "../constants";
import { accountNameFromDisplayName, generateDisplayName } from "../utils";

export type AccountCreateFormAction =
  | {
      type: "updateRegions";
      regions?: string[];
      loading?: boolean;
      error?: Error | string;
    }
  | { type: "regenerateName" }
  | {
      type: "updateForm";
      formData: Partial<
        Pick<
          AccountCreateFormState,
          | "name"
          | "isNameConflict"
          | "displayName"
          | "selectedRegion"
          | "invites"
        >
      >;
    };

export type AccountCreateFormState = {
  name: string;
  nameErr: ErrorMsg;
  isNameDirty: boolean;
  isNameConflict: boolean;
  displayName: string;
  displayNameErr: ErrorMsg;
  regions: string[];
  regionsAreLoading: boolean;
  regionsError: Error | string;
  selectedRegion: string;
  invites: string;
  invitesErr: string;
  requiredInputErr: ErrorMsg;
  submitDisabledReason: ErrorMsg;
};

export function generateNewNames() {
  const displayName = generateDisplayName();

  // There are currently 25,596 unique names that docker-names may produce.
  // We append a random number from 1-1000, yielding 25,596,000 unique account IDs
  // to further reduce the likelihood of any collisions.
  const name =
    accountNameFromDisplayName(displayName) +
    "-" +
    Math.floor(Math.random() * 1000);

  return { name, displayName };
}

export function generateInitialState(): AccountCreateFormState {
  return {
    ...generateNewNames(),
    nameErr: null,
    isNameDirty: false,
    isNameConflict: false,
    displayNameErr: null,
    regions: null,
    regionsAreLoading: true,
    regionsError: null,
    selectedRegion: "",
    invites: "",
    invitesErr: null,
    requiredInputErr: null,
    submitDisabledReason: "Account name is required",
  };
}

export function useAccountCreatePageState(
  initialState?: AccountCreateFormState
) {
  const [params, setParams] = useSearchParams();
  const plan = useMemo(
    () =>
      selfServicePlans.find((p) => p.id == params.get("selected_plan_id")) ??
      selfServicePlans.find((p) => p.id == "free"),
    [params]
  );

  function selectPlan(planId: Plan["id"]) {
    params.set(searchParamNames.selectedPlanId, planId);
    setParams(params, { replace: true });
  }

  const [formState, dispatch] = useReducer(
    (state: AccountCreateFormState, action: AccountCreateFormAction) => {
      let nextState = { ...state };

      switch (action.type) {
        case "updateForm":
          Object.assign(nextState, action.formData);

          if (action.formData.name) {
            nextState.isNameDirty = true;
          }

          if (action.formData.displayName != null && !nextState.isNameDirty) {
            nextState.name = accountNameFromDisplayName(nextState.displayName);
          }

          break;
        case "regenerateName":
          Object.assign(nextState, generateNewNames());
          nextState.isNameDirty = false;
          break;
        case "updateRegions":
          if (action.regions !== undefined) {
            nextState.regions = action.regions;
          }
          if (action.loading !== undefined) {
            nextState.regionsAreLoading = action.loading;
          }
          if (action.error !== undefined) {
            nextState.regionsError = action.error;
          }
          break;
        default: {
          const unhandled: never = action;
          throw new Error(`Unhandled action ${unhandled}`);
        }
      }

      // Unset any name conflict status when the user changes the name again
      if (nextState.name != state.name) {
        nextState.isNameConflict = false;
      }

      nextState.nameErr = validateAccountName(nextState.name);
      nextState.displayNameErr = validateDisplayName(nextState.displayName);
      nextState.invitesErr = validateInviteList(nextState.invites);
      nextState.requiredInputErr = !nextState.name
        ? "Account name is required"
        : !nextState.displayName
        ? "Display name is required"
        : !nextState.selectedRegion
        ? "Region is required"
        : null;
      nextState.submitDisabledReason =
        nextState.nameErr ??
        nextState.displayNameErr ??
        nextState.invitesErr ??
        nextState.requiredInputErr;
      return nextState;
    },
    initialState ?? generateInitialState()
  );

  return { plan, selectPlan, formState, dispatch };
}

// Per: https://docs.google.com/document/d/1S8ETdc3-D8BX_ffgjPrcVlnJmp-sTHm7a3PIg9FK-Uo/edit#heading=h.fjadff6dwmyg
const nameRegex = /^[a-z][a-z0-9]*(-[a-z0-9]+)*$/;
const nameExplanation =
  "Account names must start with a letter (a-z) and may only contain letters, numbers (0-9), or hyphens ( - ). Special characters and spaces are not allowed.";
const emailExplanation =
  "Invites must be valid email addresses separated by spaces or commas";

type ErrorMsg = string;

export function validateAccountName(name: string): ErrorMsg | null {
  if (name.length == 0) {
    return null;
  }
  return (
    (name.length >= 3 && name.length <= 50
      ? false
      : "Account name must be between 3 and 50 characters") ||
    (name.match(nameRegex) ? null : nameExplanation) ||
    null
  );
}

export function validateDisplayName(displayName: string): ErrorMsg | null {
  if (displayName.length == 0) {
    return null;
  }
  return (
    (displayName.length >= 3 && displayName.length <= 255
      ? null
      : "Display name must be between 3 and 255 characters") || null
  );
}

export function validateInviteList(invites: string): ErrorMsg | null {
  if (invites.length == 0) {
    return null;
  }
  return splitEmailList(invites).every((email: string) =>
    email.match(emailRegex)
  )
    ? null
    : emailExplanation;
}
